import React from "react";
import { ArrowRight, Building2, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import {
  getCachedSessionUser,
  isMembershipBackedSession,
} from "../pages/backend_url";
import { useLanguage } from "../../localization/LanguageContext";
import "./TenantRolloutBoundary.css";

/**
 * UX containment while legacy global modules are moved onto tenant-scoped APIs.
 * Backend authorization remains the security boundary; this component simply
 * prevents the browser from mounting known non-migrated screens.
 */
export default function TenantRolloutBoundary({
  children,
  moduleName = "This module",
  integrationName = "",
}) {
  const { copy, moduleLabel } = useLanguage();
  const user = getCachedSessionUser();
  if (!isMembershipBackedSession(user)) return children;
  const needsIntegration = Boolean(integrationName);

  return (
    <section
      className="tenant-rollout"
      aria-labelledby="tenant-rollout-title"
      data-tenant-boundary="membership"
    >
      <div className="tenant-rollout__glow" aria-hidden="true" />
      <div className="tenant-rollout__card">
        <div className="tenant-rollout__icon" aria-hidden="true">
          <Building2 size={28} />
        </div>
        <span className="tenant-rollout__eyebrow">
          <Sparkles size={15} aria-hidden="true" />
          {copy(needsIntegration ? "Account integration" : "Tenant-safe rollout")}
        </span>
        <h1 id="tenant-rollout-title">
          {needsIntegration
            ? copy("{integrationName} setup required", { integrationName })
            : copy("Module migration in progress")}
        </h1>
        {needsIntegration ? (
          <p>
            {copy("{moduleName} requires an account-owned {integrationName} connection. Shared legacy credentials are intentionally not used in this customer account because they could expose another vendor's data.", {
              moduleName: moduleLabel(moduleName),
              integrationName,
            })}
          </p>
        ) : (
          <p>
            {copy("{moduleName} is being moved to account-isolated data services. It stays closed in this membership-backed account until that migration is verified, so legacy global records are never loaded.", {
              moduleName: moduleLabel(moduleName),
            })}
          </p>
        )}
        <div className="tenant-rollout__assurance">
          <ShieldCheck size={20} aria-hidden="true" />
          <span>
            {needsIntegration
              ? copy("No provider data has been loaded. Credentials, sync state and imported records must all belong to this account before enablement.")
              : copy("Your active customer account remains isolated. Account Setup shows only the controls and read-only information enabled by the server today.")}
          </span>
        </div>
        <Link className="tenant-rollout__action" to="/AccountSetup">
          {copy(needsIntegration ? "Review Account Setup" : "Open Account Setup")}
          <ArrowRight size={17} aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}
